import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from "react-toastify";
import { apiConnector } from "../apiConnector";
import { endpoint } from "../apis";
import Cookies from 'js-cookie';

export const fetchTotalAudience = createAsyncThunk(
    "audienceInsight/fetchTotalAudience",
    async ({ selectedVideoId, filterType, start_date, end_date }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                video_id: selectedVideoId,
                filter_type: filterType
            };
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.totalAudience,
                payload,
                { Authorization: `Bearer ${token}` },
                null,
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            if (error?.response?.data === "('Token has been expired',)") {
                Cookies.remove('jwt_token');
                Cookies.remove('authToken');
                window.location.href = '/';
                toast.error('Session expired. Please log in again.');
            } else {
                toast.error(error?.response?.data || 'Failed to fetch total audience data!');
            }
            return rejectWithValue(error?.response?.data)
        }
    }
);

export const fetchTotalSubscribers = createAsyncThunk(
    "audienceInsight/fetchTotalSubscribers",
    async ({ selectedVideoId, filterType, start_date, end_date }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                video_id: selectedVideoId,
                filter_type: filterType
            };
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.newSubscribers,
                payload,
                { Authorization: `Bearer ${token}` },
                null,
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            toast.error("Failed to fetch total subscribers data");
            return rejectWithValue("Failed to fetch total subscribers data");
        }
    }
);

export const fetchNetSubscribers = createAsyncThunk(
    "audienceInsight/fetchNetSubscribers",
    async ({ selectedVideoId, filterType, start_date, end_date }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                video_id: selectedVideoId,
                filter_type: filterType
            };
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.netSubscribers,
                payload,
                {
                    Authorization: `Bearer ${token}`,
                }
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            toast.error("Failed to fetch net subscribers data");
            return rejectWithValue("Failed to fetch net subscribers data");
        }
    }
);

export const fetchSubscriberRate = createAsyncThunk(
    "audienceInsight/fetchSubscriberRate",
    async ({ selectedVideoId, filterType, start_date, end_date }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                video_id: selectedVideoId,
                filter_type: filterType
            };
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.subscriberRate,
                payload,
                { Authorization: `Bearer ${token}` },
                null,
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            toast.error("Failed to fetch subscriber rate data");
            return rejectWithValue("Failed to fetch subscriber rate data");
        }
    }
);

export const fetchAudienceRetention = createAsyncThunk(
    "audienceInsight/fetchAudienceRetention",
    async ({ selectedVideoId, filterType, start_date, end_date }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                video_id: selectedVideoId,
                filter_type: filterType
            };
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.audienceRetention,
                payload,
                { Authorization: `Bearer ${token}` },
                null,
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            toast.error("Failed to fetch audience retention data");
            return rejectWithValue("Failed to fetch audience retention data");
        }
    }
);

export const fetchAgeDistribution = createAsyncThunk(
    "audienceInsight/fetchAgeDistribution",
    async ({ selectedVideoId, filterType, selectedColumns, country_code, subscription_status, content_type, start_date, end_date }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                video_id: selectedVideoId,
                filter_type: filterType
            };

            // Add country_code if provided
            if (country_code) {
                payload.country_code = country_code;
            }
            
            // Add subscription_status if provided
            if (subscription_status) {
                payload.subscription_status = subscription_status;
            }
            
            // Add content_type if provided
            if (content_type) {
                payload.live_or_on_demand = content_type;
            }
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            // Add selectedColumns (empty array if not provided)
            payload.selected_columns = (selectedColumns && selectedColumns.length > 0) ? selectedColumns : [];
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.ageDistribution,
                payload,
                {
                    Authorization: `Bearer ${token}`,
                }
            );
            
            // Check different response formats
            const responseData = response.Response || response.data?.Response || {};
            // Extract age_distribution array from the response object
            const data = responseData.age_distribution || responseData || [];
            
            return data;
        } catch (error) {
            console.error('Age Distribution API - Error:', error);
            console.error('Age Distribution API - Error response:', error.response);
            toast.error("Failed to fetch age distribution data");
            return rejectWithValue("Failed to fetch age distribution data");
        }
    }
);

export const fetchGenderDistribution = createAsyncThunk(
    "audienceInsight/fetchGenderDistribution",
    async ({ selectedVideoId, filterType, selectedColumns, country_code, start_date, end_date, subscription_status, content_type }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                video_id: selectedVideoId,
                filter_type: filterType
            };

            // Add country_code if provided
            if (country_code) {
                payload.country_code = country_code;
            }   
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            // Add subscription_status if provided
            if (subscription_status) {
                payload.subscription_status = subscription_status;
            }
            
            // Add content_type if provided
            if (content_type) {
                payload.live_or_on_demand = content_type;
            }
            
            // Add selectedColumns (empty array if not provided)
            payload.selected_columns = (selectedColumns && selectedColumns.length > 0) ? selectedColumns : [];
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.genderDistribution,
                payload,
                {
                    Authorization: `Bearer ${token}`,
                }
            );

            // Handle different response formats
            const responseData = response.Response || response.data?.Response || {};
            // Extract gender_distribution array from the response object
            const data = responseData.gender_distribution || responseData || [];
            return data;
        } catch (error) {
            toast.error("Failed to fetch gender distribution data");
            return rejectWithValue("Failed to fetch gender distribution data");
        }
    }
);

export const fetchTopCountries = createAsyncThunk(
    "audienceInsight/fetchTopCountries",
    async ({ selectedVideoId, filterType, selectedColumns, country_code, start_date, end_date, subscription_status, content_type }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                video_id: selectedVideoId,
                filter_type: filterType
            };

            // Add country_code if provided
            if (country_code) {
                payload.country_code = country_code;
            }   
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            // Add subscription_status if provided
            if (subscription_status) {
                payload.subscription_status = subscription_status;
            }   
            
            // Add content_type if provided
            if (content_type) {
                payload.live_or_on_demand = content_type;
            }

            // Add selectedColumns (empty array if not provided)
            payload.selected_columns = (selectedColumns && selectedColumns.length > 0) ? selectedColumns : []; 
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.topCountries,
                payload,
                {
                    Authorization: `Bearer ${token}`,
                }
            );

            // Handle different response formats
            const responseData = response.Response || response.data?.Response || {};
            // Extract top_countries array from the response object
            const data = responseData.top_countries || responseData || [];
            return data;
        } catch (error) {
            toast.error("Failed to fetch top countries data");
            return rejectWithValue("Failed to fetch top countries data");
        }
    }
);

export const fetchDeviceDistribution = createAsyncThunk(
    "audienceInsight/fetchDeviceDistribution",
    async ({ selectedVideoId, filterType, selectedColumns,country_code, start_date, end_date, subscription_status, content_type }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                video_id: selectedVideoId,
                filter_type: filterType
            };

            // Add country_code if provided
            if (country_code) {
                payload.country_code = country_code;
            }   
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            // Add subscription_status if provided
            if (subscription_status) {
                payload.subscription_status = subscription_status;
            }
            
            // Add content_type if provided
            if (content_type) {
                payload.live_or_on_demand = content_type;
            }
            
            // Add selectedColumns (empty array if not provided)
            payload.selected_columns = (selectedColumns && selectedColumns.length > 0) ? selectedColumns : [];
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.deviceDistribution,
                payload,
                { Authorization: `Bearer ${token}` },
                null,
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            if (error?.response?.data === "('Token has been expired',)") {
                Cookies.remove('jwt_token');
                Cookies.remove('authToken');
                window.location.href = '/';
                toast.error('Session expired. Please log in again.');
            } else {
                toast.error(error?.response?.data || 'Failed to fetch device distribution data!');
            }
            return rejectWithValue(error?.response?.data)
        }
    }
);

export const fetchInterestCategories = createAsyncThunk(
    "audienceInsight/fetchInterestCategories",
    async ({ selectedVideoId, filterType, selectedColumns,country_code, start_date, end_date, subscription_status, content_type }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                video_id: selectedVideoId,
                filter_type: filterType
            };

            // Add country_code if provided
            if (country_code) {
                payload.country_code = country_code;
            }   
            
            // Add subscription_status if provided
            if (subscription_status) {
                payload.subscription_status = subscription_status;
            }
            
            // Add content_type if provided
            if (content_type) {
                payload.live_or_on_demand = content_type;
            }
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            // Add selectedColumns (empty array if not provided)
            payload.selected_columns = (selectedColumns && selectedColumns.length > 0) ? selectedColumns : [];
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.interestCategories,
                payload,
                { Authorization: `Bearer ${token}` },
                null,
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            if (error?.response?.data === "('Token has been expired',)") {
                Cookies.remove('jwt_token');
                Cookies.remove('authToken');
                window.location.href = '/';
                toast.error('Session expired. Please log in again.');
            } else {
                toast.error(error?.response?.data || 'Failed to fetch interest categories data!');
            }
            return rejectWithValue(error?.response?.data)
        }
    }
);

export const fetchTrafficSources = createAsyncThunk(
    "audienceInsight/fetchTrafficSources",
    async ({ selectedVideoId, filterType, selectedColumns,country_code, start_date, end_date, subscription_status, content_type }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                video_id: selectedVideoId,
                filter_type: filterType
            };

            // Add country_code if provided
            if (country_code) {
                payload.country_code = country_code;
            }   
            
            // Add subscription_status if provided
            if (subscription_status) {
                payload.subscription_status = subscription_status;
            }
            
            // Add content_type if provided
            if (content_type) {
                payload.live_or_on_demand = content_type;
            }
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            // Add selectedColumns (empty array if not provided)
            payload.selected_columns = (selectedColumns && selectedColumns.length > 0) ? selectedColumns : [];
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.trafficSources,
                payload,
                { Authorization: `Bearer ${token}` },
                null,
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            if (error?.response?.data === "('Token has been expired',)") {
                Cookies.remove('jwt_token');
                Cookies.remove('authToken');
                window.location.href = '/';
                toast.error('Session expired. Please log in again.');
            } else {
                toast.error(error?.response?.data || 'Failed to fetch traffic sources data!');
            }
            return rejectWithValue(error?.response?.data)
        }
    }
);

export const fetchSharesDistribution = createAsyncThunk(
    "audienceInsight/fetchSharesDistribution",
    async ({ selectedVideoId, filterType, selectedColumns,country_code, start_date, end_date, subscription_status, content_type }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                video_id: selectedVideoId,
                filter_type: filterType
            };

            // Add country_code if provided
            if (country_code) {
                payload.country_code = country_code;
            }   
            
            // Add subscription_status if provided
            if (subscription_status) {
                payload.subscription_status = subscription_status;
            }
            
            // Add content_type if provided
            if (content_type) {
                payload.live_or_on_demand = content_type;
            }
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            // Add selectedColumns (empty array if not provided)
            payload.selected_columns = (selectedColumns && selectedColumns.length > 0) ? selectedColumns : [];
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.sharesDistribution,
                payload,
                { Authorization: `Bearer ${token}` },
                null,
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            if (error?.response?.data === "('Token has been expired',)") {
                Cookies.remove('jwt_token');
                Cookies.remove('authToken');
                window.location.href = '/';
                toast.error('Session expired. Please log in again.');
            } else {
                toast.error(error?.response?.data || 'Failed to fetch shares distribution data!');
            }
            return rejectWithValue(error?.response?.data)
        }
    }
);

export const fetchPremiumViews = createAsyncThunk(
    "audienceInsight/fetchPremiumViews",
    async ({ selectedVideoId, filterType, selectedColumns,country_code, start_date, end_date, subscription_status, content_type }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                video_id: selectedVideoId,
                filter_type: filterType
            };
            
            // Add country_code if provided
            if (country_code) {
                payload.country_code = country_code;
            }   
            
            // Add subscription_status if provided
            if (subscription_status) {
                payload.subscription_status = subscription_status;
            }
            
            // Add content_type if provided
            if (content_type) {
                payload.live_or_on_demand = content_type;
            }
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            // Add selectedColumns (empty array if not provided)
            payload.selected_columns = (selectedColumns && selectedColumns.length > 0) ? selectedColumns : [];
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.premiumViews,
                payload,
                { Authorization: `Bearer ${token}` },
                null,
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            if (error?.response?.data === "('Token has been expired',)") {
                Cookies.remove('jwt_token');
                Cookies.remove('authToken');
                window.location.href = '/';
                toast.error('Session expired. Please log in again.');
            } else {
                toast.error(error?.response?.data || 'Failed to fetch premium views data!');
            }
            return rejectWithValue(error?.response?.data)
        }
    }
);

export const fetchBrandVideos = createAsyncThunk(
    "audienceInsight/fetchBrandVideos",
    async (_, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            const response = await apiConnector(
                "GET",
                endpoint.audience.brandVideos,
                null,
                {
                    Authorization: `Bearer ${token}`,
                }
            );

            // Parse the Response array wrapper
            let videos = [];
            
            // Handle response.data.Response format
            if (response.data && response.data.Response && Array.isArray(response.data.Response)) {
                videos = response.data.Response;
            }
            // Handle response.Response format
            else if (response.Response && Array.isArray(response.Response)) {
                videos = response.Response;
            }
            // Handle direct array format
            else if (Array.isArray(response.data)) {
                videos = response.data;
            }
            // Handle direct response array
            else if (Array.isArray(response)) {
                videos = response;
            }

            // Transform to expected format with proper field names
            const transformedVideos = videos.map(video => ({
                video_id: video.video_id,
                video_title: video.video_title,
                // Keep original fields for backward compatibility
                id: video.video_id,
                title: video.video_title
            }));

            console.log('Fetched brand videos:', transformedVideos);
            return transformedVideos;
        } catch (error) {
            console.error("Error fetching brand videos:", error);
            toast.error("Failed to fetch brand videos");
            return rejectWithValue(error.response?.data || "Failed to fetch brand videos");
        }
    }
);

export const fetchCardPerformance = createAsyncThunk(
    "audienceInsight/fetchCardPerformance",
    async ({ selectedVideoId, filterType, selectedColumns,country_code, start_date, end_date, subscription_status, content_type }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                video_id: selectedVideoId,
                filter_type: filterType
            };

            // Add subscription_status if provided
            if (subscription_status) {
                payload.subscription_status = subscription_status;
            }
            
            // Add content_type if provided
            if (content_type) {
                payload.live_or_on_demand = content_type;
            }
            
            // Add country_code if provided
            if (country_code) {
                payload.country_code = country_code;
            }   
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            // Add selectedColumns (empty array if not provided)
            payload.selected_columns = (selectedColumns && selectedColumns.length > 0) ? selectedColumns : [];
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.cardPerformance,
                payload,
                { Authorization: `Bearer ${token}` },
                null,
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            if (error?.response?.data === "('Token has been expired',)") {
                Cookies.remove('jwt_token');
                Cookies.remove('authToken');
                window.location.href = '/';
                toast.error('Session expired. Please log in again.');
            } else {
                toast.error(error?.response?.data || 'Failed to fetch card performance data!');
            }
            return rejectWithValue(error?.response?.data)
        }
    }
);

// ============================================
// GENERIC CSV DOWNLOAD FUNCTIONALITY
// ============================================

// Helper function to convert data to CSV
function convertToCSV(data, columns) {
    if (!data || data.length === 0) return '';
    
    // If columns not provided, use all keys from first object
    const cols = columns || (data.length > 0 ? Object.keys(data[0]) : []);
    
    // Create header row
    const header = cols.join(',');
    
    // Create data rows
    const rows = data.map(item => {
        return cols.map(col => {
            const value = item[col];
            // Handle values that might contain commas
            if (typeof value === 'string' && value.includes(',')) {
                return `"${value}"`;
            }
            return value !== undefined && value !== null ? value : '';
        }).join(',');
    });
    
    return [header, ...rows].join('\n');
}

// Helper function to download CSV file
function downloadCSVFile(csvContent, filename) {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

// Generic CSV download function factory
const createCSVDownloadThunk = (name, apiEndpoint, dataExtractor, columns, filename) => {
    return createAsyncThunk(
        `audienceInsight/${name}`,
        async ({ selectedVideoId, filterType, start_date, end_date }, { rejectWithValue }) => {
            const token = Cookies.get('jwt_token') || Cookies.get('authToken');

            try {
                const payload = {
                    video_id: selectedVideoId,
                    filter_type: filterType
                };
                
                if (filterType === 'custom' && start_date && end_date) {
                    payload.start_date = start_date;
                    payload.end_date = end_date;
                }
                
                const response = await apiConnector(
                    "POST",
                    apiEndpoint,
                    payload,
                    { Authorization: `Bearer ${token}` },
                    null,
                );

                // Extract data using the provided extractor function
                const data = dataExtractor(response);
                
                // Convert to CSV
                if (data && data.length > 0) {
                    const csvContent = convertToCSV(data, columns);
                    downloadCSVFile(csvContent, filename);
                    toast.success('CSV downloaded successfully!');
                } else {
                    toast.warning('No data available to download');
                }
                
                return data;
            } catch (error) {
                toast.error('Failed to download CSV');
                return rejectWithValue(error?.response?.data);
            }
        }
    );
};

// ============================================
// CSV DOWNLOAD FUNCTIONS
// ============================================

export const downloadTopCountriesCSV = createCSVDownloadThunk(
    'downloadTopCountriesCSV',
    endpoint.audience.topCountries,
    (response) => response.Response || response.data?.Response || [],
    ['country_code', 'country_name', 'views', 'percentage'],
    'top_countries.csv'
);

export const downloadTrafficSourcesCSV = createCSVDownloadThunk(
    'downloadTrafficSourcesCSV',
    endpoint.audience.trafficSources,
    (response) => response.Response?.traffic_sources || response.data?.Response?.traffic_sources || [],
    ['traffic_source', 'views', 'percentage'],
    'traffic_sources.csv'
);

export const downloadInterestCategoriesCSV = createCSVDownloadThunk(
    'downloadInterestCategoriesCSV',
    endpoint.audience.interestCategories,
    (response) => response.Response?.categories || response.data?.Response?.categories || [],
    ['category', 'views', 'percentage'],
    'interest_categories.csv'
);

export const downloadAgeDistributionCSV = createCSVDownloadThunk(
    'downloadAgeDistributionCSV',
    endpoint.audience.ageDistribution,
    (response) => response.Response || response.data?.Response || [],
    ['age_group', 'views', 'percentage'],
    'age_distribution.csv'
);

export const downloadGenderDistributionCSV = createCSVDownloadThunk(
    'downloadGenderDistributionCSV',
    endpoint.audience.genderDistribution,
    (response) => response.Response || response.data?.Response || [],
    ['gender', 'views', 'percentage'],
    'gender_distribution.csv'
);

export const downloadDeviceDistributionCSV = createCSVDownloadThunk(
    'downloadDeviceDistributionCSV',
    endpoint.audience.deviceDistribution,
    (response) => response.Response || response.data?.Response || [],
    ['device_type', 'views', 'percentage'],
    'device_distribution.csv'
);

export const downloadSharesDistributionCSV = createCSVDownloadThunk(
    'downloadSharesDistributionCSV',
    endpoint.audience.sharesDistribution,
    (response) => response.Response || response.data?.Response || [],
    ['sharing_service', 'shares', 'percentage'],
    'shares_distribution.csv'
);

export const downloadPremiumViewsCSV = createCSVDownloadThunk(
    'downloadPremiumViewsCSV',
    endpoint.audience.premiumViews,
    (response) => response.Response?.premium_views_by_source || response.data?.Response?.premium_views_by_source || [],
    ['traffic_source', 'premium_views', 'percentage'],
    'premium_views.csv'
);

export const downloadCardPerformanceCSV = createCSVDownloadThunk(
    'downloadCardPerformanceCSV',
    endpoint.audience.cardPerformance,
    (response) => response.Response || response.data?.Response || [],
    null, // Will use all keys from first object
    'card_performance.csv'
);
